import React, { useMemo, useCallback } from 'react';
import { Typography, Box, Link } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { useDispatch, useSelector } from 'react-redux';
import useDebouncedForm from 'common/hooks/useDebouncedForm';
import history from 'services/history';

import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ActiveProductActions from 'actions/ActiveProductActions';
import { useTranslation } from 'services/localization/useTranslation';
import Banner from 'components/Banner';
import { Trans } from 'services/localization';
import { Link as RouterLink } from 'react-router-dom';
import { isAutomaticDepositPaymentMethod } from 'common/modules/payments/paymentMethods';
import DepositInput from './DepositInput';
import produce from 'immer';

const DepositWrapper = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const product = useSelector(ActiveProductSelectors.productDataWithChanges);
	const isEditingDisabled = useSelector(ActiveProductSelectors.isEditingDisabled);
	const shopDepositPaymentMethods = useSelector(ShopSelectors.shopDepositPaymentMethods);
	const hasDepositPaymentMethodInUse = !!shopDepositPaymentMethods.length;

	const { state, editState } = useDebouncedForm({
		memoizedInitialState: useMemo(
			() => ({
				terms: product?.terms,
			}),
			[product],
		),
		memoizedOnDebounce: useCallback(
			(changes) => {
				dispatch(ActiveProductActions.editLocalProduct(changes));
			},
			[dispatch],
		),
	});

	const productDeposit =
		state.terms?.deposit || state?.terms?.deposit === 0 ? state?.terms?.deposit : undefined;
	const disabledBecauseNoDepositPaymentMethod =
		productDeposit == null && !hasDepositPaymentMethodInUse;
	const disabled = isEditingDisabled || disabledBecauseNoDepositPaymentMethod;

	const handleChange = (deposit: number) => {
		editState({
			terms: produce(state.terms ?? {}, (terms) => {
				if (!!deposit) {
					terms.deposit = deposit;
				} else {
					delete terms.deposit;
				}
			}),
		});
	};

	const renderBanner = () => {
		if (disabledBecauseNoDepositPaymentMethod) {
			return (
				<Box mt={2}>
					<Banner
						variant="info"
						description={
							<Typography variant="body2" color="secondary">
								<Trans
									i18nKey="product.depositSection.unavailableInfo"
									defaults="To use deposit, set up Rentle Payments or Pay on pickup under <paymentLink>payment settings</paymentLink>"
									components={{
										paymentLink: (
											<Link color="primary" component={RouterLink} to="/account/payments">
												{'payment settings'}
											</Link>
										),
									}}
								/>
							</Typography>
						}
					/>
				</Box>
			);
		}
		const noDepositPaymentMethodInUse = !shopDepositPaymentMethods.length;
		const noDepositMethodsWarning = {
			title: t(
				'account.payments.methodsDontSupportDeposit',
				"Selected payment methods don't support deposits",
			),
			description: t(
				'account.payments.methodsDontSupportDepositInfo',
				'Products with a security deposit are not available in your online store. Please enable Card payments from Rentle payments or Pay on pickup.',
			),
		};

		const onlyManualDeposit =
			!!shopDepositPaymentMethods.length &&
			!shopDepositPaymentMethods.some(isAutomaticDepositPaymentMethod);
		const onlyManualDepositWarning = {
			title: t(
				'product.depositSection.manualDepositNote',
				'You need to handle the deposit manually',
			),
			description: t(
				'product.depositSection.manualDepositDescription',
				'To reserve deposit automatically with online payment on your online store, enable card payments from Rentle Payments',
			),
		};

		const warningToShow = noDepositPaymentMethodInUse
			? noDepositMethodsWarning
			: onlyManualDeposit
			? onlyManualDepositWarning
			: null;
		if (!!warningToShow) {
			return (
				<Box mt={2}>
					<Banner
						variant="info"
						title={warningToShow.title}
						description={warningToShow.description}
						button={{
							text: t('account.payments.goToPayments', 'Go to payments'),
							onClick: () => history.push('/account/payments'),
						}}
					/>
				</Box>
			);
		}
		return null;
	};

	return (
		<ContainerBox>
			<Typography variant="h6">{t('product.deposit', 'Deposit')}</Typography>
			<Typography variant="body2" color="secondary" sx={{ mb: 4 }}>
				{t(
					'product.depositSection.depositInfo2',
					'Request a security deposit from the customer when this product is rented',
				)}
			</Typography>
			{renderBanner()}
			<Box sx={{ mt: 4, maxWidth: 600 }}>
				<DepositInput value={productDeposit} onChange={handleChange} disabled={disabled} />
			</Box>
		</ContainerBox>
	);
};

export default DepositWrapper;
